import React, { useState } from 'react';

interface Question {
    id: string;
    question: string;
    options: { label: string; value: string; icon: string }[];
}

const QUESTIONS: Question[] = [
    {
        id: 'properties',
        question: 'How many properties do you manage?',
        options: [
            { label: '1 property', value: '1', icon: 'fa-home' },
            { label: '2–5 properties', value: '2-5', icon: 'fa-building' },
            { label: '6–20 properties', value: '6-20', icon: 'fa-city' },
            { label: '21+ properties', value: '21+', icon: 'fa-building-columns' },
        ],
    },
    {
        id: 'services',
        question: 'Which services do you need most?',
        options: [
            { label: 'Compliance tracking only', value: 'compliance', icon: 'fa-clipboard-check' },
            { label: 'Tenant management + screening', value: 'tenant', icon: 'fa-users' },
            { label: 'Legal documents + evictions', value: 'legal', icon: 'fa-gavel' },
            { label: 'All of the above', value: 'all', icon: 'fa-layer-group' },
        ],
    },
    {
        id: 'support',
        question: 'What level of support do you expect?',
        options: [
            { label: 'Self-serve (docs + email)', value: 'self', icon: 'fa-book' },
            { label: 'Priority email support', value: 'priority', icon: 'fa-envelope' },
            { label: 'Dedicated account manager', value: 'dedicated', icon: 'fa-user-tie' },
            { label: 'Custom SLA + onboarding', value: 'enterprise', icon: 'fa-handshake' },
        ],
    },
];

const RECOMMENDATIONS: Record<string, { plan: string; reason: string; cta: string }> = {
    '1|compliance|self': { plan: 'Starter', reason: '1 property, compliance-only needs, self-serve support fits Starter.', cta: 'Start with Starter' },
    '1|tenant|self': { plan: 'Starter', reason: 'Single property with tenant management — Starter covers you.', cta: 'Start with Starter' },
    '1|legal|self': { plan: 'Professional', reason: 'Legal documents and evictions need Professional features.', cta: 'Upgrade to Professional' },
    '1|all|self': { plan: 'Professional', reason: 'All services on 1 property — Professional tier is optimal.', cta: 'Upgrade to Professional' },
    '2-5|compliance|self': { plan: 'Starter', reason: 'Up to 5 properties with compliance tracking fits Starter.', cta: 'Start with Starter' },
    '2-5|tenant|self': { plan: 'Starter', reason: 'Tenant management on 2–5 properties is covered by Starter.', cta: 'Start with Starter' },
    '2-5|legal|self': { plan: 'Professional', reason: 'Legal services across multiple properties needs Professional.', cta: 'Upgrade to Professional' },
    '2-5|all|self': { plan: 'Professional', reason: 'Full service stack on 2–5 properties = Professional.', cta: 'Upgrade to Professional' },
    '6-20|compliance|self': { plan: 'Starter', reason: '6–20 properties, compliance-only: Starter at scale.', cta: 'Start with Starter' },
    '6-20|tenant|self': { plan: 'Starter', reason: 'Tenant management for 6–20 properties fits Starter.', cta: 'Start with Starter' },
    '6-20|legal|self': { plan: 'Professional', reason: 'Legal services for 6–20 properties needs Professional.', cta: 'Upgrade to Professional' },
    '6-20|all|self': { plan: 'Professional', reason: 'Full suite across 6–20 properties = Professional.', cta: 'Upgrade to Professional' },
    '21+|compliance|self': { plan: 'Portfolio', reason: '21+ properties needs Portfolio tier.', cta: 'Choose Portfolio' },
    '21+|tenant|self': { plan: 'Portfolio', reason: '21+ properties with tenant management = Portfolio.', cta: 'Choose Portfolio' },
    '21+|legal|self': { plan: 'Portfolio', reason: '21+ properties with legal services = Portfolio.', cta: 'Choose Portfolio' },
    '21+|all|self': { plan: 'Portfolio', reason: 'Full service stack at 21+ properties = Portfolio.', cta: 'Choose Portfolio' },
};

const AssessmentWizard: React.FC = () => {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});

    const current = QUESTIONS[step];
    const isLast = step === QUESTIONS.length - 1;

    const handleSelect = (value: string) => {
        const next = { ...answers, [current.id]: value };
        setAnswers(next);
        if (isLast) {
            const key = [next.properties, next.services, next.support].join('|');
            const rec = RECOMMENDATIONS[key] || { plan: 'Professional', reason: 'Based on your answers, Professional is the best fit.', cta: 'Upgrade to Professional' };
            setResult(rec);
        } else {
            setStep(step + 1);
        }
    };

    const [result, setResult] = useState<{ plan: string; reason: string; cta: string } | null>(null);

    if (result) {
        return (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-check text-white text-2xl"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">We recommend: {result.plan}</h4>
                <p className="text-gray-600 mb-6">{result.reason}</p>
                <div className="flex gap-3 justify-center">
                    <button
                        onClick={() => window.location.href = `/checkout?tier=${result.plan.toLowerCase()}`}
                        className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition"
                    >
                        {result.cta}
                    </button>
                    <button
                        onClick={() => { setResult(null); setStep(0); setAnswers({}); }}
                        className="bg-white text-gray-700 border border-gray-300 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
                    >
                        Retake
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
                <span className="text-sm font-medium text-emerald-600">Question {step + 1} of {QUESTIONS.length}</span>
                <div className="flex gap-1">
                    {QUESTIONS.map((_, i) => (
                        <div key={i} className={`h-2 rounded-full ${i <= step ? 'bg-emerald-600 w-8' : 'bg-gray-200 w-4'} transition-all`}></div>
                    ))}
                </div>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">{current.question}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {current.options.map((opt) => (
                    <button
                        key={opt.value}
                        onClick={() => handleSelect(opt.value)}
                        className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-600 hover:bg-emerald-50 transition text-left"
                    >
                        <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className={`fas ${opt.icon} text-emerald-600`}></i>
                        </div>
                        <span className="font-medium text-gray-900">{opt.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default AssessmentWizard;
